import { Link } from "react-router-dom";
import { CalendarCheck, Star } from "lucide-react";
import { FadeUp, MaskedLine, Stagger, StaggerItem } from "@/components/Reveal";

import { REVIEWS, GOOGLE_RATING } from "@/data/site";

export default function Recensioni() {
    return (
        <>
            <section
                data-testid="recensioni-hero"
                className="bg-white pt-32 pb-16 text-center sm:pt-40 md:pb-20"
            >
                <div className="mx-auto max-w-3xl px-4">
                    <FadeUp y={14}>
                        <p className="flex items-center justify-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol">
                            <span className="h-px w-10 bg-petrol" />
                            Le Recensioni
                            <span className="h-px w-10 bg-petrol" />
                        </p>
                    </FadeUp>
                    <h1 className="mt-7 font-serif text-4xl font-normal leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
                        <MaskedLine delay={0.15}>Parola</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="italic text-petrol">ai pazienti.</span>
                        </MaskedLine>
                    </h1>
                    <FadeUp delay={0.5}>
                        <div
                            data-testid="recensioni-rating"
                            className="mt-8 flex flex-col items-center gap-2"
                        >
                            <div className="flex items-end gap-3">
                                <span className="font-serif text-6xl font-light leading-none text-ink">
                                    5.0
                                </span>
                                <div
                                    className="flex gap-1 pb-1.5 text-petrol"
                                    aria-label="Valutazione media 5 su 5"
                                >
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star key={i} className="h-5 w-5 fill-current" />
                                    ))}
                                </div>
                            </div>
                            <p className="text-xs font-light uppercase tracking-[0.22em] text-muted">
                                Recensioni su Google · tutte cinque stelle
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section
                data-testid="recensioni-grid-section"
                className="bg-ice py-16 sm:py-20 md:py-24"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                    <Stagger
                        testid="recensioni-grid"
                        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
                    >
                        {REVIEWS.map((r) => (
                            <StaggerItem key={r.name}>
                                <blockquote
                                    data-testid={`recensione-${r.name.replace(/\W/g, "-").toLowerCase()}`}
                                    className="flex h-full flex-col justify-between rounded-2xl border border-transparent bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-petrol/20 hover:shadow-lg"
                                >
                                    <div>
                                        <div
                                            className="flex gap-1 text-petrol"
                                            aria-label={`${r.rating} stelle su 5`}
                                        >
                                            {Array.from({ length: r.rating }).map(
                                                (_, i) => (
                                                    <Star
                                                        key={i}
                                                        className="h-4 w-4 fill-current"
                                                    />
                                                ),
                                            )}
                                        </div>
                                        <p className="mt-5 font-serif text-lg font-normal italic leading-relaxed text-ink">
                                            “{r.text}”
                                        </p>
                                    </div>
                                    <footer className="mt-8 flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-petrol-light font-serif text-base text-petrol">
                                            {r.name.charAt(0)}
                                        </span>
                                        <div>
                                            <p className="text-sm font-normal text-ink">
                                                {r.name}
                                            </p>
                                            <p className="text-xs font-light uppercase tracking-[0.18em] text-muted">
                                                {r.service}
                                            </p>
                                        </div>
                                    </footer>
                                </blockquote>
                            </StaggerItem>
                        ))}
                    </Stagger>
                    <FadeUp delay={0.2} className="mt-12 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
                        <a
                            href={GOOGLE_RATING.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="recensioni-google-link"
                            className="group inline-flex items-center gap-2 rounded-full border border-petrol px-6 py-3 text-sm font-normal text-petrol transition-all duration-300 hover:bg-petrol hover:text-white active:scale-[0.98]"
                        >
                            Leggi tutte le recensioni su Google
                        </a>
                        <a
                            href={GOOGLE_RATING.reviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="recensioni-write-link"
                            className="group inline-flex items-center gap-2 rounded-full bg-petrol px-6 py-3 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                        >
                            <Star className="h-4 w-4 fill-current" />
                            Lascia una recensione
                        </a>
                    </FadeUp>
                </div>
            </section>

            <section
                data-testid="recensioni-cta"
                className="bg-white py-20 text-center sm:py-24"
            >
                <div className="mx-auto max-w-2xl px-4">
                    <h2 className="font-serif text-3xl font-normal leading-tight text-ink sm:text-4xl">
                        <MaskedLine>La prossima storia può essere la tua.</MaskedLine>
                    </h2>
                    <FadeUp delay={0.2}>
                        <Link
                            to="/contatti"
                            data-testid="recensioni-cta-prenota"
                            className="mt-8 inline-flex items-center gap-3 rounded-full bg-petrol px-8 py-4 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                        >
                            <CalendarCheck className="h-4 w-4" />
                            Prenota la prima visita
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
